import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Settings,
  LogOut,
  Bell,
  Database,
  ChevronDown,
  Target,
  Activity,
  Sun,
  Moon,
  User as UserIcon,
  Menu,
  X,
  Radio,
  LifeBuoy,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAppSettings } from "@/contexts/AppSettingsContext";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "direktur", "manager", "admin"] },
  { to: "/leads", label: "Leads Pipeline", icon: Target, roles: ["super_admin", "direktur", "manager", "admin"] },
  { to: "/tickets", label: "Layanan & Keluhan", icon: LifeBuoy, roles: ["super_admin", "direktur", "manager", "admin"] },
  { to: "/users", label: "Users", icon: Users, roles: ["super_admin"] },
  { to: "/records", label: "Data Records", icon: Activity, roles: ["super_admin", "direktur"] },
  { to: "/online-users", label: "Online Users", icon: Radio, roles: ["super_admin", "direktur", "manager"] },
  { to: "/settings", label: "Schema Settings", icon: Database, roles: ["super_admin"] },
];

const roleLabel = {
  super_admin: "SUPER ADMIN",
  direktur: "DIREKTUR",
  manager: "MANAGER",
  admin: "ADMIN",
};

export default function Layout() {
  const { user, logout, updateMe } = useAuth();
  const appSettings = useAppSettings();
  const navigate = useNavigate();
  const [unassigned, setUnassigned] = useState(0);

  // Apply theme (light/dark) on user change
  useEffect(() => {
    const theme = user?.theme === "light" ? "light" : "dark";
    document.documentElement.classList.toggle("light", theme === "light");
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [user?.theme]);

  const toggleTheme = async () => {
    const next = user?.theme === "light" ? "dark" : "light";
    const r = await updateMe({ theme: next });
    if (!r.ok) toast.error("Gagal menyimpan tema");
  };

  // Profile dialog state
  const [profileOpen, setProfileOpen] = useState(false);
  const [pf, setPf] = useState({ name: "", avatar_url: "", password: "" });
  const openProfile = () => {
    setPf({ name: user?.name || "", avatar_url: user?.avatar_url || "", password: "" });
    setProfileOpen(true);
  };
  const saveProfile = async () => {
    const payload = { name: pf.name, avatar_url: pf.avatar_url };
    if (pf.password) payload.password = pf.password;
    const r = await updateMe(payload);
    if (r.ok) { toast.success("Profil tersimpan"); setProfileOpen(false); }
    else toast.error(r.error || "Gagal menyimpan");
  };

  useEffect(() => {
    if (!user) return;
    let stop = false;
    const fetchNotif = async () => {
      try {
        if (user.role === "super_admin" || user.role === "manager" || user.role === "direktur") {
          const { data } = await api.get("/analytics/overview");
          if (!stop) setUnassigned(data.unassigned || 0);
        } else {
          const { data } = await api.get("/analytics/me");
          if (!stop) setUnassigned(data.untouched || 0);
        }
      } catch {}
    };
    fetchNotif();
    const id = setInterval(fetchNotif, 15000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [user]);

  const initial = (user?.name || user?.email || "U").slice(0, 1).toUpperCase();

  // Mobile sidebar drawer state
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Close drawer on route change (small UX win)
  useEffect(() => {
    const handler = () => setMobileNavOpen(false);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Mobile backdrop */}
      {mobileNavOpen && (
        <div
          data-testid="mobile-nav-backdrop"
          onClick={() => setMobileNavOpen(false)}
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
        />
      )}
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed lg:static z-50 inset-y-0 left-0 w-64 shrink-0 border-r border-border bg-[#0A0A0A] flex flex-col transition-transform duration-300 lg:transition-none ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="h-16 px-4 sm:px-6 flex items-center justify-between border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            {appSettings.logo_url ? (
              <img src={appSettings.logo_url} alt="logo" className="w-8 h-8 rounded-md object-contain bg-white p-0.5 shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center font-heading font-bold text-white shrink-0">
                {(appSettings.app_name || "C").slice(0, 1)}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-heading font-semibold tracking-tight text-white leading-none text-xs truncate">
                {appSettings.app_name || "CRM"}
              </div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-1 truncate">
                {appSettings.app_subtitle || "Leads Console"}
              </div>
            </div>
          </div>
          {/* Mobile close button */}
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            className="lg:hidden p-1.5 rounded-md hover:bg-[#1C1C1E] text-muted-foreground"
            data-testid="mobile-nav-close"
            aria-label="Close menu"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems
            .filter((n) => n.roles.includes(user?.role))
            .map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMobileNavOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors group relative ${
                      isActive
                        ? "bg-[#1C1C1E] text-white before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[2px] before:bg-primary"
                        : "text-muted-foreground hover:bg-[#1C1C1E] hover:text-white"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              );
            })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground px-3 mb-2">
            Build v1.0
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 border-b border-border bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-40 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              data-testid="mobile-nav-open"
              onClick={() => setMobileNavOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-md hover:bg-[#1C1C1E] transition-colors text-muted-foreground"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                Workspace
              </div>
              <div className="font-heading font-semibold text-white text-base truncate">
                Student Leads CRM
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className="p-2 rounded-md hover:bg-[#1C1C1E] transition-colors"
              title="Toggle theme"
            >
              {user?.theme === "light" ? (
                <Moon className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
              ) : (
                <Sun className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
              )}
            </button>
            <button
              data-testid="notification-bell"
              onClick={() => navigate(user?.role === "admin" ? "/leads" : "/leads")}
              className="relative p-2 rounded-md hover:bg-[#1C1C1E] transition-colors"
            >
              <Bell className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
              {unassigned > 0 && (
                <span
                  data-testid="notification-count"
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-[10px] font-mono font-bold text-white flex items-center justify-center"
                >
                  {unassigned > 99 ? "99+" : unassigned}
                </span>
              )}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[#1C1C1E] transition-colors"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&w=64" />
                    <AvatarFallback className="bg-primary/20 text-primary text-xs font-semibold">
                      {initial}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left hidden md:block">
                    <div className="text-sm text-white font-medium leading-none">
                      {user?.name}
                    </div>
                    <div className="text-[10px] font-mono tracking-wider text-muted-foreground mt-1">
                      {roleLabel[user?.role] || user?.role}
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#121212] border-border">
                <DropdownMenuLabel>
                  <div className="text-sm text-white">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="profile-menu" onClick={openProfile} className="cursor-pointer">
                  <UserIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Profile settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={() => {
                    logout();
                    navigate("/login");
                  }}
                  className="text-destructive focus:text-destructive cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-grid-pattern">
          <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Profile dialog */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="bg-[#121212] border-border">
          <DialogHeader><DialogTitle className="font-heading">Profile settings</DialogTitle></DialogHeader>
          <div className="flex justify-center mb-2">
            <Avatar className="w-20 h-20">
              <AvatarImage src={pf.avatar_url} />
              <AvatarFallback className="bg-primary/20 text-primary text-2xl">
                {(pf.name || "U").slice(0, 1).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Nama</label>
              <Input data-testid="profile-name-input" value={pf.name} onChange={(e) => setPf({ ...pf, name: e.target.value })} className="bg-[#0A0A0A] border-border" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Foto profil (URL)</label>
              <Input data-testid="profile-avatar-input" value={pf.avatar_url} onChange={(e) => setPf({ ...pf, avatar_url: e.target.value })} placeholder="https://..." className="bg-[#0A0A0A] border-border" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Password baru (opsional)</label>
              <Input type="password" placeholder="Kosongkan jika tidak diubah" value={pf.password} onChange={(e) => setPf({ ...pf, password: e.target.value })} className="bg-[#0A0A0A] border-border" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileOpen(false)} className="border-border bg-transparent">Cancel</Button>
            <Button data-testid="save-profile-button" onClick={saveProfile} className="bg-primary hover:bg-[#0056B3]">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

  );
}
